import type { SidecarMap3 } from '../../../../dorian-shared/types/episodeScript';
import { IDENTITY_ALIGNMENT } from './lineAlignment';
import type { LineAlignment } from './lineAlignment';

/**
 * Basil v0.2, P1 and P2 — the episode's own first example, read off the script.
 *
 * Nothing here writes: "Show an example" moves the caret and lights one line the writer already
 * has. No example text is ever inserted into the episode, and a card that cannot find a form in
 * this episode falls back to the words the brief's own example uses rather than inventing one.
 *
 * Every value is taken from the SCRIPT, not from the engine, so the names a writer sees in the card
 * are the names they see on the page — including the moment the book supplies none and the script
 * keeps an id.
 */

/** The speaker label: `LILLY (Happy)`, `NOAH`, `LILLY (Happy) [thinking]`. */
const SPEAKER_LINE = /^([A-Z][A-Z0-9 _.-]*?)(\s+\([^)]+\))?(\s+\[[a-z]+\])?$/;

/**
 * The engine's own spelling of a character, mood or location (T17-05).
 *
 * The decompiler seeds its names table with the id's own spelling, so a book that cannot name a
 * thing leaves `C4894851` standing in the script where a word belongs. No id ever reaches a writer,
 * so a form carrying one is explained in ordinary words instead of being shown.
 */
const ID_SPELLING = /^[CEL]-?\d+$/i;

/** True when this line is a speaker label rather than prose that happens to be in capitals. */
export function isSpeakerLabel(line: string): boolean {
  const spelled = String(line ?? '').trim();

  return spelled !== '' && SPEAKER_LINE.test(spelled);
}

const linesOf = (text: string): string[] => String(text ?? '').replace(/\r\n?/g, '\n').split('\n');

/** Where "Show an example" goes, and which of the two line forms it found. */
export interface ExampleTarget {
  /** the 1-based line the caret lands on and the overlay lights. */
  line: number;
  /** `dialogue` when the episode has a speaker; `narration` when it does not. */
  kind: 'dialogue' | 'narration';
}

/**
 * The episode's first dialogue line, or its first narration line when no line has a speaker.
 *
 * A character block starts with its speaker label and carries the words underneath, which is what
 * the card says; so the caret goes to the WORDS, one line under the label, and the label stays in
 * view above it. A character step with no label of its own (a reaction, a continued line) is its
 * own first line already.
 */
export function exampleTarget(
  map: SidecarMap3 | null | undefined,
  text: string,
  align?: LineAlignment,
): ExampleTarget | null {
  const lines = linesOf(text);
  const where = align ?? IDENTITY_ALIGNMENT;
  // T17-04: the caret lands in the text the writer is looking at, so every candidate is asked where
  // it is NOW. A block whose position cannot be established is not a line to point at, and it is
  // dropped rather than guessed at.
  const blocks: Array<{ kind: string; at: number }> = [];

  (map?.blocks ?? []).forEach((block) => {
    if (block.kind !== 'character' && block.kind !== 'narration') {
      return;
    }
    const at = where.current(Number(block.line));

    if (at != null) {
      blocks.push({ kind: block.kind, at });
    }
  });

  if (blocks.length === 0) {
    return null;
  }
  const ordered = blocks.slice().sort((left, right) => left.at - right.at);
  const spoken = ordered.find((block) => block.kind === 'character');
  const block = spoken ?? ordered[0];
  const { at } = block;
  const own = lines[at - 1];
  const labelled = block.kind === 'character' && typeof own === 'string' && SPEAKER_LINE.test(own.trim());
  const line = labelled && typeof lines[at] === 'string' ? at + 1 : at;

  return { line, kind: block.kind === 'character' ? 'dialogue' : 'narration' };
}

/** Which of the four forms is the brief's example rather than a line of this episode. */
export interface BorrowedForms {
  heading: boolean;
  speaker: boolean;
  choice: boolean;
  goto: boolean;
}

/** Which of this episode's names could not be spelled, so the card explains them in words. */
export interface UnavailableNames {
  location: boolean;
  character: boolean;
  mood: boolean;
}

/** The four line forms, spelled with this episode's own words wherever the episode has them. */
export interface ScriptForms {
  /** `== intro @ Kitchen`, or `== intro` where the scene names no location it can spell. */
  heading: string;
  /** `intro` */
  sceneName: string;
  /** `Kitchen`, or null where the scene names no location the card may show. */
  locationName: string | null;
  /** `LILLY (Happy)`, or `LILLY` where the line carries no mood the card may show. */
  speaker: string;
  /** `* Take the key. → Hall` */
  choice: string;
  /** `Hall` — where that answer leads. */
  choiceTarget: string;
  /** `> go to: Hall` */
  goto: string;
  /** `Hall` — where the story continues. */
  gotoTarget: string;
  /** Choice 12: a borrowed form is labelled as an example, never offered as this episode's line. */
  borrowed: BorrowedForms;
  /** T17-05: a name the book cannot spell is explained, never printed as the engine's id. */
  unavailable: UnavailableNames;
}

/** What the card says where the episode itself has no such line. The brief's own example. */
export const FALLBACK_FORMS: ScriptForms = Object.freeze({
  heading: '== intro @ Kitchen',
  sceneName: 'intro',
  locationName: 'Kitchen',
  speaker: 'LILLY (Happy)',
  choice: '* Take the key. → Hall',
  choiceTarget: 'Hall',
  goto: '> go to: Hall',
  gotoTarget: 'Hall',
  borrowed: Object.freeze({
    heading: true, speaker: true, choice: true, goto: true,
  }),
  unavailable: Object.freeze({ location: false, character: false, mood: false }),
}) as ScriptForms;

/** The arrow the script spells an answer's destination with. */
const ARROW = '→';

/**
 * Read the four forms off the script.
 *
 * Each form is looked for on its own: an episode with scenes and dialogue but no choice keeps its
 * own heading and speaker and borrows only the choice. Nothing is composed out of parts — every
 * line the card shows is a line that stands somewhere in this episode, or the fallback entire.
 */
export function scriptForms(text: string): ScriptForms {
  const lines = linesOf(text);
  const forms: ScriptForms = {
    ...FALLBACK_FORMS,
    borrowed: {
      heading: true, speaker: true, choice: true, goto: true,
    },
    unavailable: { location: false, character: false, mood: false },
  };
  const heading = lines.find((line) => /^\s*==\s*\S/.test(line));

  if (heading) {
    const spelled = heading.trim();
    const inner = spelled.replace(/^==\s*/, '');
    const at = inner.lastIndexOf(' @ ');
    const sceneName = (at < 0 ? inner : inner.slice(0, at)).trim();
    const locationName = at < 0 ? null : inner.slice(at + 3).trim();

    if (ID_SPELLING.test(sceneName)) {
      // The scene itself has no word. There is nothing of this heading left to teach from, so the
      // brief's example stands in and says so.
      forms.unavailable.location = locationName != null && ID_SPELLING.test(locationName);
    } else {
      forms.borrowed.heading = false;
      forms.sceneName = sceneName;

      if (locationName != null && ID_SPELLING.test(locationName)) {
        // T17-05: the location has no word. The heading is shown WITHOUT it rather than with the
        // engine's spelling of it, and the card explains the gap in ordinary words underneath.
        forms.unavailable.location = true;
        forms.locationName = null;
        forms.heading = `== ${sceneName}`;
      } else {
        forms.heading = spelled;
        forms.locationName = locationName;
      }
    }
  }

  const speaker = lines.find((line) => isSpeakerLabel(line));

  if (speaker) {
    const spelled = speaker.trim();
    const parts = spelled.match(SPEAKER_LINE);
    const name = parts ? parts[1].trim() : spelled;
    const mood = parts && parts[2] ? parts[2].trim() : '';
    const moodWord = mood.replace(/^\(|\)$/g, '').trim();

    if (ID_SPELLING.test(name)) {
      // A speaker label that is an id teaches nothing and shows an id. The example stands in.
      forms.unavailable.character = true;
      forms.unavailable.mood = moodWord !== '' && ID_SPELLING.test(moodWord);
    } else if (moodWord !== '' && ID_SPELLING.test(moodWord)) {
      forms.borrowed.speaker = false;
      forms.unavailable.mood = true;
      forms.speaker = name;
    } else {
      forms.borrowed.speaker = false;
      forms.speaker = spelled;
    }
  }

  const choice = lines.find((line) => /^\s*\*/.test(line) && line.indexOf(ARROW) !== -1);

  if (choice) {
    const spelled = choice.trim();
    const target = spelled.slice(spelled.indexOf(ARROW) + 1).trim();

    if (!ID_SPELLING.test(target)) {
      forms.borrowed.choice = false;
      forms.choice = spelled;
      forms.choiceTarget = target;
    }
  }

  const goto = lines.find((line) => /^\s*>\s*go to:/.test(line));

  if (goto) {
    const spelled = goto.trim();
    const target = spelled.replace(/^>\s*go to:\s*/, '').trim();

    if (!ID_SPELLING.test(target)) {
      forms.borrowed.goto = false;
      forms.goto = spelled;
      forms.gotoTarget = target;
    }
  }

  return forms;
}
